import { reactive } from 'vue'
import { fetchCategorias, fetchCategoria, fetchAlergenos, fetchIdiomas } from './api'
import { isoToday, isRealPhoto } from './data'

// ---------- persistencia en localStorage ----------
function load(key, def) {
  try {
    const v = localStorage.getItem(key)
    return v === null ? def : JSON.parse(v)
  } catch (e) {
    return def
  }
}
function save(key, val) {
  try {
    localStorage.setItem(key, JSON.stringify(val))
  } catch (e) { /* almacenamiento no disponible */ }
}

// Prefijo de despliegue (publicPath). El buffet va en la ruta: <base>/<id>.
const BASE = (process.env.BASE_URL || '/').replace(/\/+$/, '')

export const store = reactive({
  // ---- preferencias persistentes ----
  locale: load('seth_tpc_locale', null) || 'es',
  dia: load('seth_tpc_dia', null) || isoToday(),
  mine: load('seth_tpc_mine', []),

  // Caché persistente id de alérgeno → icono_url. La API solo devuelve los
  // iconos de los alérgenos referenciados en cada categoría, así que los
  // acumulamos para tenerlos disponibles también en "Mis alérgenos".
  iconCache: load('seth_tpc_icons', {}),

  // ---- buffet (categoría) activo, gobernado por la URL ----
  catId: null,
  categoria: null, // { id, nombre, descripcion } de la respuesta

  // ---- UI efímera ----
  query: '',
  onlyApto: false,
  selectedFamilia: null, // null = "Todas"
  view: 'menu', // 'menu' | 'mine'
  detailId: null,
  isWide: typeof window !== 'undefined' ? window.innerWidth >= 760 : true,

  // ---- idiomas disponibles (selector dinámico) ----
  idiomas: [], // [{ locale, nombre, bandera }]

  // ---- catálogo de alérgenos (sección "Mis alérgenos") ----
  alergenosCatalogo: [], // [{ id, nombre, codigo, nota, icono_url }] ordenado
  alergenoIndex: {}, // { [id]: entrada del catálogo }

  // ---- datos de la API ----
  categorias: [],
  escandalls: [],
  familias: [], // ordenadas por `orden`
  alergenosMap: {}, // { [id]: { id, nombre, icono_url } }
  booting: true, // arranque inicial (cargando lista de buffets)
  loading: false,
  error: null
})

// Descarta respuestas obsoletas si el usuario cambia rápido de día/buffet/idioma.
let reqSeq = 0

// ============================================================
// Routing (History API): el buffet activo se codifica en /<id>.
// ============================================================
export function parseRoute() {
  let path = window.location.pathname
  if (BASE && path.startsWith(BASE)) path = path.slice(BASE.length) || '/'
  const m = path.match(/^\/(\d+)\/?$/)
  return m ? parseInt(m[1], 10) : null
}

function applyRoute() {
  const id = parseRoute()
  store.view = 'menu'
  store.detailId = null
  if (id == null) {
    clearBuffet()
    return
  }
  // Si ya conocemos la lista y el id no existe, volvemos a la pantalla inicial.
  if (store.categorias.length && !store.categorias.some(c => c.id === id)) {
    navigateLanding(true)
    return
  }
  store.catId = id
  loadDishes()
}

export function navigateCat(id) {
  window.history.pushState({}, '', `${BASE}/${id}`)
  store.catId = id
  store.view = 'menu'
  store.detailId = null
  loadDishes()
}

export function navigateLanding(replace = false) {
  const url = `${BASE}/`
  if (replace) window.history.replaceState({}, '', url)
  else window.history.pushState({}, '', url)
  clearBuffet()
}

function clearBuffet() {
  store.catId = null
  store.categoria = null
  store.escandalls = []
  store.familias = []
  store.alergenosMap = {}
  store.selectedFamilia = null
  store.view = 'menu'
}

export function initRouter() {
  window.addEventListener('popstate', applyRoute)
}

// ============================================================
// Carga de datos
// ============================================================
export async function init() {
  initRouter()
  store.booting = true
  try {
    await loadIdiomas() // fija/valida el locale antes de pedir datos traducidos
    await Promise.all([loadCategorias(), loadAlergenos()])
  } finally {
    store.booting = false
  }
  applyRoute()
}

export async function loadIdiomas() {
  try {
    const list = await fetchIdiomas()
    store.idiomas = Array.isArray(list) ? list : []
    // Si el idioma guardado ya no está disponible, usar el base (el primero).
    if (store.idiomas.length && !store.idiomas.some(l => l.locale === store.locale)) {
      store.locale = store.idiomas[0].locale
      save('seth_tpc_locale', store.locale)
    }
  } catch (e) {
    store.error = e
  }
}

export async function loadAlergenos() {
  try {
    const list = await fetchAlergenos(store.locale)
    store.alergenosCatalogo = Array.isArray(list) ? list : []
    const idx = {}
    for (const a of store.alergenosCatalogo) idx[a.id] = a
    store.alergenoIndex = idx
    mergeIconCache(idx)
  } catch (e) {
    store.error = e
  }
}

export async function loadCategorias() {
  try {
    const cats = await fetchCategorias()
    store.categorias = Array.isArray(cats) ? cats : []
  } catch (e) {
    store.error = e
  }
}

export async function loadDishes() {
  if (store.catId == null) return
  const seq = ++reqSeq
  store.loading = true
  store.error = null
  try {
    const data = await fetchCategoria(store.catId, store.dia, store.locale)
    if (seq !== reqSeq) return // respuesta obsoleta
    store.categoria = data.categoria || null
    store.escandalls = Array.isArray(data.escandalls) ? data.escandalls : []
    store.alergenosMap = data.alergenos || {}
    mergeIconCache(store.alergenosMap)
    store.familias = Object.values(data.familias || {})
      .sort((a, b) => (a.orden - b.orden) || a.nombre.localeCompare(b.nombre))
    // Conserva la familia seleccionada si sigue presente; si no, "Todas".
    if (store.selectedFamilia != null && !store.familias.some(f => f.id === store.selectedFamilia)) {
      store.selectedFamilia = null
    }
  } catch (e) {
    if (seq !== reqSeq) return
    store.error = e
    store.escandalls = []
    store.familias = []
    store.alergenosMap = {}
  } finally {
    if (seq === reqSeq) store.loading = false
  }
}

// Acumula los icono_url del mapa de alérgenos en la caché persistente.
function mergeIconCache(map) {
  if (!map) return
  let changed = false
  const next = { ...store.iconCache }
  for (const a of Object.values(map)) {
    if (a && a.id != null && isRealPhoto(a.icono_url) && next[a.id] !== a.icono_url) {
      next[a.id] = a.icono_url
      changed = true
    }
  }
  if (changed) {
    store.iconCache = next
    save('seth_tpc_icons', next)
  }
}

// ---------- acciones ----------
export function setLocale(code) {
  store.locale = code
  save('seth_tpc_locale', code)
  loadAlergenos() // recarga el catálogo traducido
  loadDishes() // la API traduce nombres según locale
}
export function setDia(n) {
  store.dia = n
  save('seth_tpc_dia', n)
  store.selectedFamilia = null
  loadDishes()
}
export function setFamilia(id) { store.selectedFamilia = id }
export function setQuery(v) { store.query = v }
export function clearQuery() { store.query = '' }
export function setOnlyApto(v) { store.onlyApto = !!v }
export function openDish(id) { store.detailId = id }
export function closeDish() { store.detailId = null }
export function toggleView() {
  store.view = store.view === 'mine' ? 'menu' : 'mine'
  store.detailId = null
}
export function gotoMenu() { store.view = 'menu' }
export function toggleMine(id) {
  store.mine = store.mine.includes(id)
    ? store.mine.filter(x => x !== id)
    : [...store.mine, id]
  save('seth_tpc_mine', store.mine)
}
export function clearMine() {
  store.mine = []
  save('seth_tpc_mine', [])
}
export function setWide(v) { store.isWide = v }

export function retry() {
  if (!store.categorias.length) init()
  else loadDishes()
}
